import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import MediaCard from '@/components/media/MediaCard';
import { speaker, formatById } from '@/data/speaker';

const whatsappInvite = `https://wa.me/${speaker.contact.whatsapp}?text=${encodeURIComponent(
  'Olá, Johnny! Gostaria de te convidar para palestrar em um evento.',
)}`;

export function SpeakingHero() {
  return (
    <section className="speak-hero" aria-labelledby="speaking-title">
      <p className="pulse-eyebrow">Palestras · Workshops · Treinamento in-company</p>
      <h1 id="speaking-title">INTELIGÊNCIA<br />NÃO SE BAIXA.<br /><span>SE FORMA.</span></h1>
      <p className="speak-hero-lead">
        A tecnologia é o motor sob o capô; o resultado é humano. Levo essa conversa para o palco em
        três recortes — e para dentro das equipes, quando o evento pede continuidade.
      </p>
      <div className="speak-hero-actions">
        <a className="pulse-button" href="#convite">Convidar para palestrar <ArrowRight size={19} aria-hidden="true" /></a>
        <Link className="pulse-text-link" to="/imprensa">Bios e fotos para imprensa <ArrowUpRight size={18} aria-hidden="true" /></Link>
      </div>
      <div className="speak-hero-meta">
        {speaker.formats.map((format) => <span key={format.id}>{format.name} · {format.duration}</span>)}
      </div>
    </section>
  );
}

export function TopicsDetail() {
  return (
    <section id="temas" className="pulse-section" aria-labelledby="topics-title">
      <div className="pulse-section-heading">
        <div>
          <p className="pulse-eyebrow">Temas</p>
          <h2 id="topics-title">O QUE A PLATEIA<br />LEVA EMBORA.</h2>
        </div>
        <p>Cada tema nasceu de um problema real — dentro de uma equipe, de um sistema em produção ou de uma sala de aula.</p>
      </div>
      {speaker.topics.map((topic, index) => (
        <article key={topic.id} className="speak-topic">
          <div>
            <p className="speak-topic-index">{String(index + 1).padStart(2, '0')}</p>
            <h3>{topic.title}</h3>
            <p className="speak-topic-promise">{topic.promise}</p>
          </div>
          <div>
            <p className="speak-takeaways-label">O que fica</p>
            <ul className="speak-takeaways">
              {topic.takeaways.map((takeaway) => <li key={takeaway}>{takeaway}</li>)}
            </ul>
            <div className="speak-topic-meta">
              <span>Para: {topic.forWhom}</span>
              <span>Formatos: {topic.formatIds.map((id) => formatById(id)?.name).join(' · ')}</span>
            </div>
          </div>
        </article>
      ))}
    </section>
  );
}

export function Formats() {
  return (
    <section id="formatos" className="pulse-section" aria-labelledby="formats-title">
      <div className="pulse-section-heading">
        <div>
          <p className="pulse-eyebrow">Formatos</p>
          <h2 id="formats-title">DO PALCO<br />À SALA DA EQUIPE.</h2>
        </div>
        <p>O mesmo conteúdo muda de profundidade conforme o tempo disponível e o que o evento precisa deixar para trás.</p>
      </div>
      <div className="speak-formats">
        {speaker.formats.map((format) => (
          <article key={format.id} className="speak-format">
            <h3>{format.name}</h3>
            <p className="speak-format-duration">{format.duration}</p>
            <p>{format.description}</p>
          </article>
        ))}
      </div>
      <ul className="speak-prep" style={{ marginTop: 'clamp(2.5rem, 5vw, 4rem)' }}>
        <li><strong>Recorte combinado antes</strong><span>Ajusto o tema ao público do evento — o que muda é o exemplo, não a tese.</span></li>
        <li><strong>Material da organização</strong><span>Bio, fotos em alta e título aprovado ficam prontos em <Link to="/imprensa" className="pulse-text-link" style={{ display: 'inline', minHeight: 0 }}>/imprensa</Link>.</span></li>
        <li><strong>Requisitos simples</strong><span>Projeção com HDMI, microfone e, para workshop, os participantes com notebook.</span></li>
      </ul>
    </section>
  );
}

export function MediaAndCredentials() {
  return (
    <section id="midia" className="pulse-section" aria-labelledby="speaking-media-title">
      <div className="pulse-section-heading">
        <div>
          <p className="pulse-eyebrow">Na mídia e reconhecimentos</p>
          <h2 id="speaking-media-title">ONDE EU<br />JÁ ESTIVE.</h2>
        </div>
        <p>Entrevistas e conversas públicas sobre IA aplicada, automação e carreira em tecnologia.</p>
      </div>
      <div className="speak-media-grid">
        {speaker.media.map((item) => <MediaCard key={item.link} {...item} />)}
      </div>
      <ul className="speak-credentials">
        {speaker.credentials.map((credential) => (
          <li key={credential.title} className="speak-credential">
            <span className="speak-credential-year">{credential.year}</span>
            <div><strong>{credential.title}</strong><span>{credential.detail}</span></div>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function InviteSection({ children }: { children?: React.ReactNode }) {
  return (
    <section id="convite" className="pulse-section speak-invite" aria-labelledby="invite-title">
      <div className="speak-invite-grid">
        <div>
          <p className="pulse-eyebrow">Convite</p>
          <h2 id="invite-title">QUAL É O<br /><span>SEU EVENTO?</span></h2>
          <p className="speak-invite-lead">
            Me conte o formato, a data e quem estará na plateia. Respondo com a disponibilidade e
            uma proposta de recorte para o seu público.
          </p>
          <p className="speak-invite-alt">
            Prefere conversar direto?{' '}
            <a href={whatsappInvite} target="_blank" rel="noopener noreferrer">WhatsApp</a>
            {' '}ou{' '}
            <a href={`mailto:${speaker.contact.email}?subject=${encodeURIComponent('Convite para palestra')}`}>{speaker.contact.email}</a>.
          </p>
        </div>
        <div>{children}</div>
      </div>
    </section>
  );
}
